import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import {
  COOKIE_MODE,
  DEFAULT_SETTINGS,
  api,
  clearAdminPassword,
  isAuthenticated,
  onAccessChanged,
  onUnauthorised,
  type ApiSettings,
} from './api';
import { AppLayout } from './components/AppLayout';
import { LoginScreen } from './components/LoginScreen';
import { useLocalStorage } from './hooks/useLocalStorage';
import { applyDocumentLang, t } from './i18n';
import { DEFAULT_PATH } from './navigation';
import { AlertScreen } from './screens/AlertScreen';
import { DashboardScreen } from './screens/DashboardScreen';
import { FieldManagementScreen } from './screens/FieldManagementScreen';
import { LogScreen } from './screens/LogScreen';
import { MasterScreen } from './screens/MasterScreen';
import { MonitorScreen } from './screens/MonitorScreen';
import { ReportScreen } from './screens/ReportScreen';
import { LegacyRulesRedirect, RuleSetRulesRoute } from './screens/RuleSetRoutes';
import type { Session } from './session';
// R9 cases
import { InvestigateScreen } from './screens/InvestigateScreen';
// R8 users
import { ForcedPasswordChange } from './components/PasswordChange';
import { UsersScreen } from './screens/UsersScreen';
import { ApiKeysScreen } from './screens/ApiKeysScreen'; // Wave 4 API clients
// 2c rule sets
import { RuleSetsScreen } from './screens/RuleSetsScreen';

/**
 * Three phases, because cookie mode cannot answer "am I signed in?" locally.
 *
 *   checking — cookie mode only: `GET /api/session` is in flight. The shell must NOT mount
 *              yet: `useRuleSetDraft` fires `GET /api/rules` the moment `AppLayout` renders,
 *              and racing that against the session probe means a cold start flashes the
 *              console and then a 401.
 *   login    — no session (or password mode with nothing in sessionStorage).
 *   ready    — the console, with `session` non-null in cookie mode and null in password mode.
 */
type Phase = 'checking' | 'login' | 'ready' | 'password';

/**
 * R8 users: a session holding a temporary password goes to the change-password screen, not
 * the console — the admin service refuses everything else until it is changed.
 */
const phaseFor = (s: Session | null | undefined): Phase => (!s ? 'login' : s.mustChangePassword ? 'password' : 'ready');

/** Same person, same roles, same password state — nothing for the shell to redraw. */
const sameAccess = (a: Session | null, b: Session | null): boolean =>
  a !== null &&
  b !== null &&
  a.user.id === b.user.id &&
  !!a.mustChangePassword === !!b.mustChangePassword &&
  [...a.roles].sort().join(',') === [...b.roles].sort().join(',');

export function App() {
  const [stored] = useLocalStorage<ApiSettings>('kkp.rule-admin.settings', DEFAULT_SETTINGS);
  // A stable object identity, or every render would look like a settings change to the hook.
  const settings = useMemo<ApiSettings>(() => ({ baseUrl: stored.baseUrl ?? '' }), [stored.baseUrl]);

  // Password mode: a credential in sessionStorage means "signed in for this tab", and the
  // answer is available synchronously. Cookie mode has to ask the BFF.
  const [phase, setPhase] = useState<Phase>(() => (COOKIE_MODE ? 'checking' : isAuthenticated() ? 'ready' : 'login'));
  const [session, setSession] = useState<Session | null>(null);
  const [loginMessage, setLoginMessage] = useState<string | null>(null);

  // `<html lang>` follows the active locale; English is the default.
  useEffect(() => {
    applyDocumentLang();
  }, []);

  // The cold-start probe. "Nobody is signed in yet" — `200 {authenticated:false}`, or a 401
  // from an older BFF — comes back as `null` and carries no message: `api.session` does not
  // fire `onUnauthorised` for exactly that reason. A failed probe also lands on the login.
  useEffect(() => {
    if (!COOKIE_MODE) return;
    let cancelled = false;
    void (async () => {
      try {
        const s = await api.session(settings);
        if (!cancelled) {
          setSession(s);
          setPhase(phaseFor(s));
        }
      } catch {
        if (!cancelled) setPhase('login');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [settings]);

  // A 401 from ANY call — not only the login — comes back here. QA-A-06: a session the
  // admin service ended because the password was changed or reset elsewhere says so.
  useEffect(() => {
    onUnauthorised((reason) => {
      setSession(null);
      setPhase('login');
      setLoginMessage(
        t(!COOKIE_MODE ? 'auth.badPassword.message' : reason === 'credentials_changed' ? 'auth.revoked.credentials' : 'auth.expired.message'),
      );
    });
    return () => onUnauthorised(null);
  }, []);

  // QA-A-12: what this session may do changed (a role removed, a password reset) — re-read it,
  // so the navigation and the screens follow at once. One probe at a time; the probe itself
  // (`GET /api/session`) never answers 403, so this cannot loop.
  const refreshing = useRef(false);
  useEffect(() => {
    if (!COOKIE_MODE) return;
    onAccessChanged(() => {
      if (refreshing.current) return;
      refreshing.current = true;
      void (async () => {
        try {
          const s = await api.session(settings);
          // Only a real change re-renders the shell: a screen whose call keeps answering 403
          // must not be re-mounted into asking again.
          setSession((prev) => (sameAccess(prev, s) ? prev : s));
          setPhase(phaseFor(s));
        } catch {
          /* the screen already shows the refusal; the next call will try again */
        } finally {
          refreshing.current = false;
        }
      })();
    });
    return () => onAccessChanged(null);
  }, [settings]);

  const signOut = useCallback(async () => {
    if (COOKIE_MODE) {
      // Server-side first: the session record is the truth, so the cookie is dead at once.
      // A failure here still signs the tab out — leaving the console up would be worse.
      try {
        await api.logout(settings);
      } catch {
        /* the session may already be gone; the UI must still return to the login screen */
      }
    } else {
      clearAdminPassword();
    }
    setSession(null);
    setPhase('login');
    setLoginMessage(null);
  }, [settings]);

  if (phase === 'checking') {
    return (
      <div className="login-screen">
        <div className="login-body">
          <div className="login-card">
            <div className="empty" data-testid="session-checking">
              {t('shell.session.checking')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'login') {
    return (
      <LoginScreen
        settings={settings}
        message={loginMessage}
        onAuthenticated={(next) => {
          setLoginMessage(null);
          setSession(next ?? null);
          // Password mode hands no session up and has no temporary passwords.
          setPhase(next ? phaseFor(next) : 'ready');
        }}
      />
    );
  }

  // R8 users: the second half of signing in with a temporary password.
  if (phase === 'password' && session) {
    return (
      <ForcedPasswordChange
        settings={settings}
        session={session}
        onSignOut={() => void signOut()}
        onChanged={() => {
          void (async () => {
            try {
              const s = await api.session(settings);
              setSession(s);
              setPhase(phaseFor(s));
            } catch {
              setPhase('login');
            }
          })();
        }}
      />
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          element={<AppLayout settings={settings} session={session} onSignOut={() => void signOut()} />}
        >
          <Route index element={<Navigate to={DEFAULT_PATH} replace />} />
          <Route path="dashboard" element={<DashboardScreen />} />
          {/*
            Wave 5: the rule set is the workspace — a set's page with its Transaction and
            Pattern base tabs (remounted per set and tab, see RuleSetRulesRoute). The old
            rule screens redirect there: `?set=` names the set, the primary otherwise.
          */}
          <Route path="rule-sets" element={<RuleSetsScreen />} />
          <Route path="rule-sets/:setId" element={<Navigate to="transaction" replace />} />
          <Route path="rule-sets/:setId/transaction" element={<RuleSetRulesRoute kind="rb" />} />
          <Route path="rule-sets/:setId/pattern" element={<RuleSetRulesRoute kind="pb" />} />
          <Route path="rules/transaction" element={<LegacyRulesRedirect kind="rb" />} />
          <Route path="rules/pattern" element={<LegacyRulesRedirect kind="pb" />} />
          <Route path="rules/sets" element={<Navigate to="/rule-sets" replace />} />
          <Route path="fields" element={<FieldManagementScreen />} />
          <Route path="masters" element={<MasterScreen />} />
          <Route path="masters/:id" element={<MasterScreen />} />
          <Route path="report" element={<ReportScreen />} />
          <Route path="alerts" element={<AlertScreen />} />
          <Route path="monitor" element={<MonitorScreen />} />
          <Route path="log" element={<LogScreen />} />
          {/* R9 cases */}
          <Route path="investigate" element={<InvestigateScreen />} />
          <Route path="investigate/:cardId/:caseId" element={<InvestigateScreen />} />
          {/* R8 users: Admin only (the screen refuses anyone else; so does the admin service). */}
          <Route path="users" element={<UsersScreen />} />
          {/* Wave 4 API clients: Admin only (the screen refuses anyone else; so does the admin service). */}
          <Route path="api-keys" element={<ApiKeysScreen />} />
          {/* An unknown deep link lands somewhere real rather than on a blank shell. */}
          <Route path="*" element={<Navigate to={DEFAULT_PATH} replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
